import {css} from '@mui/material';
import {DlMuiDynamicIcon, TooltipWrapperCore} from '@wallaby-ui/core';
import React from 'react';
import {useIntl} from 'react-intl';

import {AlertRowFlagDefinitionDto} from './alert_types';
import {AlertCategoryIcon} from './AlertCategoryIcon';
import {AlertWeightBadge} from './AlertWeightBadge';

type Props = {
  /** The alerts whose episode on this row displays as Active, highest weight first (the BE orders them). */
  alerts: AlertRowFlagDefinitionDto[];
};

const bellCss = css`
  font-size: 14px;
`;
const wrapperCss = css`
  display: inline-flex;
  align-items: center;
  color: var(--dlColorWarning);
  cursor: default;
`;
/* the tooltip is a white card like the design, not the dark popper every other tooltip uses */
const popperCss = css`
  max-width: 272px;
  width: 272px;
  background-color: #fff;
  color: var(--dlThemeGray-800);
  border: 1px solid var(--dlThemeGray-100);
  border-radius: 12px;
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.2);
  padding: 12px 13px;
  line-height: normal;
  text-align: left;
`;
const headerCss = css`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 8px;
  color: var(--dlColorWarning);
`;
const headerIconCss = css`
  font-size: 12px;
`;
const headerTitleCss = css`
  font-size: 11px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-family: var(--dlFontFamilySemiBold);
  color: var(--dlThemeGray-600);
`;
const rowCss = css`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 5px 0;
`;
const rowIconCss = css`
  display: inline-flex;
  justify-content: center;
  flex: 0 0 auto;
  width: 14px;
  color: var(--dlThemeGray-400);
`;
const rowNameCss = css`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: var(--dlThemeGray-800);
  font-family: var(--dlFontFamilySemiBold);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * The red bell of a list row that matches at least one alert (PDW-240). Plain in the first release, no
 * count on the icon. Hover opens the list of matched alert definitions: header "Alerts · N", then one line
 * per alert with the category icon, the name and the weight badge, in the BE's order (weight descending).
 * Renders nothing for a row without alerts; whether the column exists at all is the list's decision from
 * the user's Use alerts right, never from the response.
 */
export const AlertRowBell: React.FC<Props> = (props) => {
  const {alerts} = props;
  const intl = useIntl();
  if (!alerts.length) return null;
  const tooltip = (
    <div data-name={'dl-alert-row-bell--tooltip'}>
      <div css={headerCss}>
        <DlMuiDynamicIcon
          iconName='bellIcon'
          useFixedViewBox='0 0 448 512'
          noButtonStyles
          style={{color: 'inherit', padding: 0, margin: 0, pointerEvents: 'none'}}
          svgIconInputCss={headerIconCss}
        />
        <span css={headerTitleCss}>{intl.formatMessage({id: 'alert.row.tooltip.title'}, {count: alerts.length})}</span>
      </div>
      {alerts.map((alert) => (
        <div css={rowCss} key={alert.userFilterId} data-name={'dl-alert-row-bell--alert'}>
          <span css={rowIconCss}>
            <AlertCategoryIcon isGlobal={alert.isGlobal} />
          </span>
          <span css={rowNameCss} title={alert.name}>
            {alert.name}
          </span>
          <AlertWeightBadge weight={alert.weight} variant='tooltip' />
        </div>
      ))}
    </div>
  );
  return (
    <TooltipWrapperCore tooltip={tooltip} noPadding wrapperCss={wrapperCss} popperInputCss={popperCss} placement='bottom-start'>
      <DlMuiDynamicIcon
        iconName='bellIcon'
        useFixedViewBox='0 0 448 512'
        noButtonStyles
        style={{color: 'inherit', padding: 0, margin: 0, pointerEvents: 'none'}}
        svgIconInputCss={bellCss}
        data-name={'dl-alert-row-bell'}
        data-count={alerts.length}
      />
    </TooltipWrapperCore>
  );
};

AlertRowBell.displayName = 'AlertRowBell';
